import { useEffect, useState } from "react";
import { Box, Button, Dialog, CircularProgress } from "@mui/material";
import { fetchInstructorClassDetails } from "../../../api/apiClient";
import { formatSessionTimeRange } from "../../studentSessionHelpers";
import { ROLE_LABEL, categoryColor, plural, sessionLabel, shortDate, shortDow } from "./scheduleModel";
import { Icon, FactLine, CategoryTile, CategoryLabel, TOKENS as T } from "./scheduleUi";

const SHEET_ICON = <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>;
const TEST_ICON = <><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4M9 13h6M9 17h4" /></>;

function Section({ title, count, children }) {
  return (
    <Box component="section" sx={{ py: "18px", "& + &": { borderTop: `1px solid ${T.hairline}` } }}>
      <Box component="h3" sx={{ display: "flex", alignItems: "center", gap: "8px", m: "0 0 10px", font: "600 11px var(--font-ui)", letterSpacing: ".5px", textTransform: "uppercase", color: T.muted }}>
        {title}
        {count != null && (
          <Box component="span" sx={{ minWidth: 18, height: 18, px: "5px", borderRadius: "9px", fontSize: 11, display: "inline-grid", placeItems: "center", bgcolor: T.press, color: T.ink, letterSpacing: 0 }}>{count}</Box>
        )}
      </Box>
      {children}
    </Box>
  );
}

const None = ({ children }) => <Box sx={{ fontSize: 13, color: T.muted }}>{children}</Box>;

function AttachedItem({ icon, color, title, detail }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr)", gap: "12px", alignItems: "center", py: "10px", "& + &": { borderTop: `1px solid ${T.hairline}` } }}>
      <Box aria-hidden="true" sx={{ width: 32, height: 32, borderRadius: "8px", display: "grid", placeItems: "center", color, bgcolor: `color-mix(in oklch, ${color} 11%, #fff)` }}>
        <Icon size={16}>{icon}</Icon>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box component="b" sx={{ display: "block", fontWeight: 500, fontSize: 13.5 }}>{title}</Box>
        {detail && <Box component="small" sx={{ display: "block", fontSize: 12, color: T.muted }}>{detail}</Box>}
      </Box>
    </Box>
  );
}

const join = (parts) => parts.filter(Boolean).join(" · ");

/**
 * What the instructor would be teaching: the class description, its sessions, and the skill sheets and tests attached
 * to it. `target.sessionIds` limits the list to the sessions on the card (null = every session of the class).
 */
export default function ClassDetailsDialog({ target, tenantId, onClose }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    Promise.resolve(fetchInstructorClassDetails(target.classId, tenantId))
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: null }); })
      .catch((e) => {
        if (!cancelled) setState({ loading: false, data: null, error: e?.response?.data?.message || "Could not load the class details." });
      });
    return () => { cancelled = true; };
  }, [target, tenantId]);

  if (!target) return null;
  const { data } = state;
  const shown = data ? data.sessions.filter((s) => !target.sessionIds || target.sessionIds.includes(s.id)) : [];
  const shownIds = new Set(shown.map((s) => s.id));
  const nameById = new Map(shown.map((s, i) => [s.id, sessionLabel({ sessionName: s.name, topics: s.topics, sessionNumber: s.sessionNumber }) || `Session ${i + 1}`]));
  const inScope = (item) => item.sessionId == null || shownIds.has(item.sessionId);
  const scope = (item) => (item.sessionId == null ? "All sessions" : nameById.get(item.sessionId));
  const sheets = data ? data.skillSheets.filter(inScope) : [];
  const tests = data ? data.tests.filter(inScope) : [];
  const showScope = shown.length > 1;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth={false}
      aria-labelledby="class-details-title"
      PaperProps={{ sx: { width: "min(640px, calc(100vw - 32px))", maxHeight: "calc(100vh - 64px)", borderRadius: "16px", border: `1px solid ${T.hairline}`, boxShadow: "var(--shadow-card-md)" } }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "44px minmax(0,1fr) auto", gap: "14px", alignItems: "start", p: "20px 24px 16px", borderBottom: `1px solid ${T.hairline}` }}>
        <CategoryTile color={categoryColor(target.category)} />
        <Box sx={{ minWidth: 0 }}>
          <Box component="h2" id="class-details-title" sx={{ m: "0 0 4px", fontSize: 19, fontWeight: 700 }}>{target.courseTitle}</Box>
          <FactLine>
            {target.courseNumber && <Box component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{target.courseNumber}</Box>}
            <CategoryLabel category={target.category} />
            {target.role && <span>{ROLE_LABEL[target.role] || target.role}</span>}
          </FactLine>
        </Box>
        <Box component="button" type="button" aria-label="Close details" onClick={onClose}
          sx={{ width: 34, height: 34, borderRadius: "10px", border: 0, bgcolor: "transparent", display: "grid", placeItems: "center", color: T.muted, cursor: "pointer", "&:hover": { bgcolor: T.press, color: T.ink } }}>
          <Icon size={18}><path d="M18 6 6 18M6 6l12 12" /></Icon>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: "4px 24px 24px" }}>
        {state.loading && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={24} aria-label="Loading class details" /></Box>}
        {state.error && <Box role="alert" sx={{ py: 4, color: T.danger, fontSize: 13.5 }}>{state.error}</Box>}
        {data && (
          <>
            <Section title="About this class">
              {data.description ? <Box sx={{ fontSize: 14, whiteSpace: "pre-line" }}>{data.description}</Box> : <None>No description yet.</None>}
            </Section>

            <Section title="Sessions" count={shown.length}>
              <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, border: `1px solid ${T.hairline}`, borderRadius: "10px" }}>
                {shown.map((s, i) => (
                  <Box key={s.id} component="li" sx={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr)", gap: "10px", p: "12px 14px", "& + &": { borderTop: `1px solid ${T.hairline}` } }}>
                    <Box component="span" sx={{ font: "500 12px var(--font-mono)", color: T.muted, pt: "1px" }}>{String(i + 1).padStart(2, "0")}</Box>
                    <Box>
                      <Box component="b" sx={{ display: "block", fontWeight: 600, fontSize: 13.5 }}>{nameById.get(s.id)}</Box>
                      <Box component="small" sx={{ display: "block", fontSize: 12.5, color: T.muted, mt: "1px" }}>
                        {join([s.sessionDate ? `${shortDow(s.sessionDate)}, ${shortDate(s.sessionDate)}` : null, formatSessionTimeRange(s), s.location || (s.sessionType === "VIRTUAL" ? "Online" : null)])}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Section>

            <Section title="Skill check sheets" count={sheets.length}>
              {sheets.length === 0 ? <None>No skill sheets for this class.</None> : sheets.map((s) => (
                <AttachedItem key={`s${s.id}`} icon={SHEET_ICON} color="var(--color-success)" title={s.title}
                  detail={join([s.count != null ? plural(s.count, "skill") : null, showScope ? scope(s) : null])} />
              ))}
            </Section>

            <Section title="Tests" count={tests.length}>
              {tests.length === 0 ? <None>No tests for this class.</None> : tests.map((t) => (
                <AttachedItem key={`t${t.id}`} icon={TEST_ICON} color="var(--color-accent-violet)" title={t.title}
                  detail={join([t.count != null ? plural(t.count, "question") : null, t.passingScore != null ? `${t.passingScore}% to pass` : null,
                    t.timeLimitMinutes ? `${t.timeLimitMinutes} min` : null, showScope ? scope(t) : null])} />
              ))}
            </Section>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "14px 24px", borderTop: `1px solid ${T.hairline}`, bgcolor: T.wash }}>
        <Box component="span" sx={{ mr: "auto", fontSize: 12.5, color: T.muted }}>Materials open once you are assigned.</Box>
        <Button onClick={onClose}
          sx={{ height: 38, px: "16px", borderRadius: "10px", fontSize: 12, fontWeight: 600, letterSpacing: ".2px", textTransform: "uppercase", bgcolor: "#fff", color: T.ink, border: `1px solid ${T.hairlineCool}`, "&:hover": { borderColor: T.ink, bgcolor: T.press } }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
}
